/*
 * ******************************************************************
 * Copyright (c) 2021-2024 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */

import { ComponentRef, Injectable } from "@angular/core";

import { Observable } from "rxjs";
import { TaskV2 } from "@api/generated/vcdr";
import { DialogAnchorDirective } from "@shared/components/dialog/dialog-anchor/dialog-anchor.directive";
import { ClosableDialog } from "./closable-dialog";
import { ConfirmationDialog, ConfirmationDialogType } from "./confirmation-dialog/confirmation-dialog";
import { MessageDialog } from "./message-dialog/message-dialog";

// @formatter:off
@Injectable({
    providedIn: "root"
})
// @formatter:on
export class DialogService {
    private dialogAnchor: DialogAnchorDirective;

    public setDialogAnchor(anchor: DialogAnchorDirective): void {
        this.dialogAnchor = anchor;
    }

    /**
     * Use this method to dynamically instantiate wizards and dialogs.
     * The dialog creator needs to call dialog.close to enable angular to free its resources.
     * @param dialogClass - the wizard/dialog class to be instantiated.
     * @returns {ClosableDialog}
     */
    public createDialog(
        dialogClass: { new(...args: any[]): ClosableDialog; }): ClosableDialog {
        const dialogRef: ComponentRef<ClosableDialog> =
            this.dialogAnchor.createDialog(dialogClass);
        return dialogRef.instance;
    }

    /**
     * Shows a Yes/No confirmation dialog.
     * @param title - a localizable string to use as a dialog title.
     * @param message - a localizable string to use as a detailed explanation/question.
     * @param cancelBtnText? - @optional - a localizable string to use as cancel button text.
     * @param confirmationBtnText? - @optional - a localizable string to use as
     *                                          confirmation button text.
     * @param confirmationBtnStyle? - @optional - style to use on confirmation button.
     * @param executeOperation? - @optional - function to trigger the actual operation.
     * @param validateOperation? - @optional - validate function to be executed before allowing the user to trigger
     *                                        the actual operation.
     * @param type? - @optional - show different icons in front of the message.
     * @param messageParams? - @optional - parameters part of the message.
     * @param conditions? - @optional - conditions that need to be confirmed by the user.
     * @returns {EventEmitter<boolean>} - returns true if the dialog was closed by pressing
     *                                    the 'yes' button, false - otherwise.
     */
    public showConfirmationDialog(title: string,
                                  message: string,
                                  cancelBtnText?: string,
                                  confirmationBtnText?: string,
                                  confirmationBtnStyle?: string,
                                  executeOperation?: Observable<any>,
                                  validateOperation?: Observable<any>,
                                  type?: ConfirmationDialogType,
                                  customIcon?: string,
                                  conditions?: string[]): Observable<TaskV2> {

        const dialog = this.createConfirmationDialog(title, message, cancelBtnText, confirmationBtnText, confirmationBtnStyle, type,
                                                     customIcon, conditions, executeOperation, validateOperation);
        dialog.showDialog();

        return dialog.outputTask;
    }

    public showSyncConfirmationDialog(title: string,
                                  message: string,
                                  cancelBtnText?: string,
                                  confirmationBtnText?: string,
                                  confirmationBtnStyle?: string,
                                  executeOperation?: Observable<any>,
                                  validateOperation?: Observable<any>,
                                  type?: ConfirmationDialogType,
                                  customIcon?: string,
                                  conditions?: string[]): Observable<boolean> {
        const dialog = this.createConfirmationDialog(title, message, cancelBtnText, confirmationBtnText, confirmationBtnStyle, type,
                                                     customIcon, conditions, executeOperation, validateOperation);
        dialog.showDialog();

        return dialog.confirmed;
    }

    private createConfirmationDialog(title: string,
                      message: string,
                      cancelBtnText: string,
                      confirmationBtnText: string,
                      confirmationBtnStyle: string,
                      type: ConfirmationDialogType,
                      customIcon: string,
                      conditions: string[],
                      executeOperation: Observable<any>,
                      validateOperation: Observable<any>): ConfirmationDialog {
        const dialog: ConfirmationDialog = <ConfirmationDialog>this.createDialog(ConfirmationDialog);
        dialog.title = title;
        dialog.message = message;
        if (cancelBtnText != null) {
            dialog.cancelBtnText = cancelBtnText;
        }
        if (confirmationBtnText != null) {
            dialog.confirmationBtnText = confirmationBtnText;
        }
        if (confirmationBtnStyle != null) {
            dialog.confirmationBtnStyle = confirmationBtnStyle;
        }
        if (type != null) {
            dialog.type = type;
        }
        if (customIcon != null) {
            dialog.customIcon = customIcon;
        }
        if (conditions) {
            dialog.conditions = conditions;
        }
        if (executeOperation) {
            dialog.executeOperation = executeOperation;
        }
        if (validateOperation) {
            dialog.validateOperation = validateOperation;
        }
        return dialog;
    }

    /**
     * Shows a message dialog.
     * @param title - a localizable string to use as a dialog title.
     * @param message - a localizable string to use as a detailed explanation.
     * @param iconShape - a dr-icon shape.
     * @param confirmationBtnText - @optional - a localizable string to use as
     *                                          confirmation button text.
     * @param confirmationBtnStyle - @optional - style to use on confirmation button.
     * @param innerHTML - set to true if the message should be formatted as HTML
     * @param messageParams - supports up to 3 message parameters to be substituted in the message string
     *                        The limitation comes from the Angular pipe support and the way pipe parameters are provided.
     */
    public showMessageDialog(title: string,
                             message: string,
                             confirmationBtnText?: string,
                             confirmationBtnStyle?: string,
                             iconShape?: string,
                             innerHTML?: boolean,
                             titleParams?: any[],
                             messageParams?: any[]): Observable<boolean> {
        const dialog: MessageDialog = <MessageDialog>this.createDialog(MessageDialog);
        dialog.title = title;
        dialog.message = message;
        dialog.iconShape = iconShape;
        dialog.innerHTML = innerHTML;
        dialog.messageParams = messageParams ? messageParams : [];
        dialog.titleParams = titleParams ? titleParams : [];
        if (confirmationBtnText != null) {
            dialog.confirmationBtnText = confirmationBtnText;
        }
        if (confirmationBtnStyle != null) {
            dialog.confirmationBtnStyle = confirmationBtnStyle;
        }
        dialog.showDialog();
        return dialog.confirmed;
    }
}
